import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Library, Send, Package, FileText, DollarSign, Users, Receipt, ArrowRight } from "lucide-react";

const STAGE_META = {
  draft: ["Draft", "bg-slate-400"],
  ready: ["Ready", "bg-blue-500"],
  approved: ["Approved", "bg-violet-500"],
  scheduled: ["Scheduled", "bg-amber-500"],
  published: ["Published", "bg-emerald-500"],
  archived: ["Archived", "bg-slate-600"],
};

export default function OpsDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/ops/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  const biz = [
    [DollarSign, "Revenue", `$${(d.business.revenue || 0).toLocaleString()}`],
    [Receipt, "Paid orders", d.business.orders],
    [Users, "Leads", d.business.leads],
    [FileText, "Controlled docs", d.documents.total],
  ];
  const maxSys = Math.max(1, ...d.documents.systems.map((s) => s.count));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Executive Dashboard</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Real-time status across content, products, documents and revenue.</p>
      </div>

      {/* Business snapshot */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {biz.map(([Icon, label, val]) => (
          <div key={label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <Icon className="w-5 h-5 text-indigo-500 mb-3" />
            <div className="font-heading font-black text-2xl" data-testid={`ops-metric-${label.split(" ")[0].toLowerCase()}`}>{val}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Content pipeline */}
        <Card title="Content pipeline" icon={Send} to="/ops/publishing" cta="Open queue" testid="ops-pipeline">
          {d.content.total === 0 ? (
            <Empty>No content yet. Add your first post or article.</Empty>
          ) : (
            <div className="space-y-2.5">
              {d.meta.stages.filter((s) => s !== "archived").map((s) => {
                const n = d.publishing[s] || 0;
                const pct = d.content.total ? Math.round((n / d.content.total) * 100) : 0;
                const [label, color] = STAGE_META[s];
                return (
                  <div key={s}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600 dark:text-slate-300">{label}</span>
                      <span className="font-semibold">{n}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(n ? 4 : 0, pct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Content by type */}
        <Card title="Content by channel" icon={Library} to="/ops/content" cta="Open library" testid="ops-bytype">
          {Object.keys(d.content.by_type).length === 0 ? (
            <Empty>No content items yet.</Empty>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(d.content.by_type).map(([t, n]) => (
                <span key={t} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 capitalize">{t} · {n}</span>
              ))}
            </div>
          )}
        </Card>

        {/* Products */}
        <Card title="Product Center" icon={Package} to="/admin/products" cta="Manage products" testid="ops-products">
          <div className="flex items-end gap-6">
            <Stat n={d.products.ready} label="Ready" color="text-emerald-500" />
            <Stat n={d.products.draft} label="Draft" color="text-amber-500" />
            <Stat n={d.products.total} label="Total" color="" />
          </div>
        </Card>
      </div>

      {/* Document coverage by system */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-500" />
            <h2 className="font-heading font-bold">Document coverage by operating system</h2>
          </div>
          <Link to="/admin/documents" className="text-sm text-indigo-500 hover:underline inline-flex items-center gap-1">
            Document Control <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="space-y-3">
          {d.documents.systems.map((s) => (
            <div key={s.code}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-mono">{s.code}</span>
                <span className="font-semibold">{s.count} docs</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round((s.count / maxSys) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, to, cta, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-indigo-500" />
        <h2 className="font-heading font-bold text-sm">{title}</h2>
      </div>
      <div className="flex-1">{children}</div>
      {to && (
        <Link to={to} className="text-sm text-indigo-500 hover:underline inline-flex items-center gap-1 mt-4">
          {cta} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

function Stat({ n, label, color }) {
  return (
    <div>
      <div className={`font-heading font-black text-3xl ${color}`}>{n}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-slate-400 py-4">{children}</p>;
}
